import { websiteMedia as media, websiteImageProps } from '../../../util/website_media';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

const defaultPhotos = [
  [media.lochFrontFacade.src, media.lochFrontFacade.alt],
  [media.stanwichArchitecturalLiving.src, media.stanwichArchitecturalLiving.alt],
  [media.kingdomChefKitchen.src, media.kingdomChefKitchen.alt],
  [media.waterfrontLiving.src, media.waterfrontLiving.alt],
  [media.originalCityLiving.src, media.originalCityLiving.alt],
  [media.originalStaircaseView.src, media.originalStaircaseView.alt]
];
const Gallery = styled.section`
  max-width: 1360px; margin: 0 auto; padding: 36px 40px 72px; scroll-margin-top: 100px; color: #1b264f; text-align: center;
  .gallery-eyebrow { font: 700 14px/1.5 Poppins, sans-serif; color: #2c5bff; text-transform: uppercase; letter-spacing: .16em; }
  h2 { text-transform: none; color: #1b264f; font: 600 clamp(28px, 3vw, 42px)/1.2 Poppins, sans-serif; letter-spacing: -.035em; max-width: 730px; margin: 12px auto 20px; }
  .gallery-description { font: 400 17px/1.6 'Nunito Sans', sans-serif; color: #52617e; margin: 20px 0 24px; }
  .photo-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
  .image-card { display: block; width: 100%; border: 0; padding: 0; background: #f5f7fa; border-radius: 12px; overflow: hidden; position: relative; cursor: zoom-in; text-align: center; }
  .image-card img { width: 100%; height: 300px; object-fit: cover; display: block; transition: transform .25s; }
  .image-card:hover img { transform: scale(1.025); }
  button:focus-visible, a:focus-visible { outline: 3px solid #2c5bff; outline-offset: 4px; }
  dialog { border: 0; padding: 0; background: transparent; width: 100vw; max-width: 100vw; height: 100dvh; max-height: 100dvh; margin: 0; overflow: hidden; color: white; }
  dialog::backdrop { background: rgba(8, 12, 22, .95); }
  .gallery-lightbox { display: flex; align-items: center; justify-content: center; height: 100%; padding: 64px; }
  dialog img { display: block; max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; }
  .viewer-control { position: absolute; border: 0; background: transparent; color: white; cursor: pointer; font: 400 36px/1 sans-serif; padding: 12px; min-width: 48px; min-height: 48px; }
  .viewer-close { top: 10px; right: 12px; }
  .viewer-prev { left: 8px; top: calc(50% - 24px); }
  .viewer-next { right: 8px; top: calc(50% - 24px); }
  @media (max-width: 600px) { .gallery-lightbox { padding: 60px 40px; } .viewer-prev { left: 0; } .viewer-next { right: 0; } }
  @media (max-width: 991px) { padding: 36px 40px 72px; .photo-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 767px) {
    padding: 24px 20px 44px;
    .photo-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .image-card img { height: 180px; }
  }
  @media (prefers-reduced-motion: reduce) { .image-card img { transition: none; } }
`;

export default function MediaGallery({ id = 'homepage-work', title = 'The details make the difference.', eyebrow = 'A closer look', description = 'Interiors, exteriors, and the details that make a home memorable.', photos = defaultPhotos }) {
  const [selected, setSelected] = useState(null);
  const dialog = useRef(null);
  const openImage = (index) => { setSelected(index); dialog.current.showModal(); };
  const nextImage = (step) => setSelected(index => (index + step + photos.length) % photos.length);

  return (
    <Gallery id={id} aria-labelledby={id + '-title'}>
      <p className="gallery-eyebrow">{eyebrow}</p>
      <h2 id={id + '-title'}>{title}</h2>
      <p className="gallery-description">{description}</p>
      <div className="photo-grid">
        {photos.map(([src, description], index) => (
          <button type="button" className="image-card" key={description} onClick={() => openImage(index)} aria-label={`Enlarge ${description}`}>
            <img {...websiteImageProps(src, '(max-width: 767px) 50vw, (max-width: 991px) 50vw, 33vw')} src={src} alt={description} loading="lazy" />
          </button>
        ))}
      </div>
      <dialog ref={dialog} aria-label="Photography gallery" onKeyDown={event => { if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); nextImage(event.key === 'ArrowRight' ? 1 : -1); } }}>
        <div className="gallery-lightbox" onClick={event => { if (event.target === event.currentTarget) dialog.current.close(); }}>
          {selected !== null && <img src={photos[selected][0]} alt={photos[selected][1]} />}
        </div>
        <button type="button" className="viewer-control viewer-close" aria-label="Close gallery" onClick={() => dialog.current.close()}>×</button>
        <button type="button" className="viewer-control viewer-prev" aria-label="Previous photo" onClick={() => nextImage(-1)}>‹</button>
        <button type="button" className="viewer-control viewer-next" aria-label="Next photo" onClick={() => nextImage(1)}>›</button>
      </dialog>
    </Gallery>
  );
}